import React from 'react';
import { PasswordInput as ArkPasswordInput } from '@ark-ui/react/password-input';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export type PasswordInputProps = BaseInputProps<string> & {
  /**
   * Lokalizovaný popisek přepínače viditelnosti — dostane aktuální stav.
   * Default je český; aplikace s i18n předává přeložený text.
   */
  visibilityLabel?: (visible: boolean) => string;
  className?: string;
};

/**
 * Atomický input pro heslo postavený na Ark UI `PasswordInput`.
 *
 * Obsahuje vestavěný přepínač viditelnosti (`VisibilityTrigger`). Zag jej
 * defaultně vyřazuje z tab orderu (`tabIndex: -1`, vzor z nativních password
 * reveal ovládání) — vracíme `tabIndex: 0`, aby byl dosažitelný i z klávesnice,
 * a doplňujeme `onClick` pro aktivaci Enterem/Mezerníkem (zag reaguje jen na
 * `pointerdown`, který klávesnice negeneruje; u myši `pointerdown` proběhne
 * dřív a `preventDefault` zabrání dvojímu přepnutí).
 */
export const PasswordInput: React.FC<PasswordInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  placeholder,
  describedBy,
  testAttributes,
  visibilityLabel = (visible) => (visible ? 'Skrýt heslo' : 'Zobrazit heslo'),
  className,
}) => (
  <ArkPasswordInput.Root
    name={name}
    ids={{ input: id }}
    disabled={disabled}
    readOnly={readOnly}
    invalid={hasError}
    translations={{ visibilityTrigger: visibilityLabel }}
    className={clsx(className)}
  >
    <ArkPasswordInput.Control className={styles.passwordControl}>
      <ArkPasswordInput.Input
        ref={ref}
        name={name}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        className={styles.passwordInput}
        aria-describedby={describedBy}
        {...testAttributes}
      />
      <ArkPasswordInput.Context>
        {(api) => (
          <ArkPasswordInput.VisibilityTrigger
            className={styles.passwordTrigger}
            tabIndex={0}
            onClick={(event: React.MouseEvent) => {
              // detail === 0 → aktivace klávesnicí (Enter/Space); myš už
              // přepnula přes zag pointerdown handler.
              if (event.detail === 0) api.toggleVisible();
            }}
          >
            <ArkPasswordInput.Indicator fallback={<Icon name="eye-slash" size={18} />}>
              <Icon name="eye" size={18} />
            </ArkPasswordInput.Indicator>
          </ArkPasswordInput.VisibilityTrigger>
        )}
      </ArkPasswordInput.Context>
    </ArkPasswordInput.Control>
  </ArkPasswordInput.Root>
);

PasswordInput.displayName = 'PasswordInput';
